/**
 * RechargeModal — modal de recarga de créditos (cartão + PIX)
 * Design: identidade visual ChatGPT (clean, minimalista, espaçoso)
 */
import { T } from '../../styles/tokens';
import { useEffect, useState } from 'preact/hooks';
import { Signal } from '@preact/signals';
import { Spinner } from './Spinner';

interface RechargeModalProps {
  showModal: Signal<boolean>;
  balance: Signal<number | null>;
  api: {
    get: <T = any>(url: string) => Promise<T>;
    post: <T = any>(url: string, body: any) => Promise<T>;
  };
}

type RechargeState = 'idle' | 'processing' | 'success' | 'pix_waiting' | 'error';
interface SavedCard { id: string; name: string; last4: string; brand: string; exp_month: number; exp_year: number; }

export function RechargeModal({ showModal, balance, api }: RechargeModalProps) {
  const [state, setState] = useState<RechargeState>('idle');
  const [amount, setAmount] = useState(10);
  const [tab, setTab] = useState<'card' | 'pix'>('pix');
  const [cards, setCards] = useState<SavedCard[]>([]);
  const [selectedCard, setSelectedCard] = useState<string | null>(null);
  const [useNewCard, setUseNewCard] = useState(false);
  const [cardForm, setCardForm] = useState({ number: '', exp_month: '', exp_year: '', cvv: '', holder_name: '' });
  const [saveCard, setSaveCard] = useState(true);
  const [errorMsg, setErrorMsg] = useState('');
  const [pixCode, setPixCode] = useState('');
  const [pixQrUrl, setPixQrUrl] = useState('');
  const [pixTimer, setPixTimer] = useState(1800);
  const [copied, setCopied] = useState(false);
  const [ready, setReady] = useState(false);
  const [isFirstDeposit, setIsFirstDeposit] = useState(false);
  const [brlRate, setBrlRate] = useState(5.80);
  const isOpen = showModal.value;

  useEffect(() => {
    if (isOpen) {
      setReady(false);
      Promise.all([
        api.get<any>('/wallet/cards').catch(() => ({ cards: [] })),
        api.get<any>('/wallet').catch(() => null),
        api.get<any>('/subscription/exchange-rate').catch(() => null),
      ]).then(([cardsRes, walletRes, rateRes]: any[]) => {
        if (rateRes?.rate && typeof rateRes.rate === 'number') setBrlRate(rateRes.rate);
        const c = cardsRes?.cards || [];
        setCards(c);
        if (c.length > 0) { setSelectedCard(c[0].id); setUseNewCard(false); }
        else setUseNewCard(true);
        if (walletRes?.wallet?.balance != null) balance.value = walletRes.wallet.balance;
        setReady(true);
      });
    } else {
      setReady(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (state !== 'pix_waiting') return;
    const iv = setInterval(() => setPixTimer(t => t > 0 ? t - 1 : 0), 1000);
    return () => clearInterval(iv);
  }, [state]);

  const close = () => { showModal.value = false; setState('idle'); setErrorMsg(''); setPixCode(''); setPixQrUrl(''); setPixTimer(1800); setUseNewCard(false); setReady(false); };

  const handlePay = async () => {
    setState('processing');
    try {
      const body: any = { amount, paymentMethod: tab === 'card' ? 'credit_card' : 'pix' };
      if (tab === 'card') {
        if (!useNewCard && selectedCard && cards.length > 0) {
          body.savedCardId = selectedCard;
        } else {
          body.cardData = { number: cardForm.number.replace(/\s/g, ''), exp_month: parseInt(cardForm.exp_month), exp_year: parseInt(cardForm.exp_year), cvv: cardForm.cvv, holder_name: cardForm.holder_name };
          body.saveCard = saveCard;
        }
      }
      const res = await api.post<any>('/wallet/topup', body);
      if (res?.firstDeposit) setIsFirstDeposit(true);
      if (tab === 'pix' && (res?.qrCodeText || res?.qrCode || res?.status === 'pending_payment')) {
        setPixCode(res.qrCodeText || '');
        setPixQrUrl(res.qrCode || '');
        setState('pix_waiting');
        if (res?.orderId) {
          const pollInterval = setInterval(async () => {
            try {
              const statusRes = await api.get<any>(`/wallet/topup/${res.orderId}/status`);
              if (statusRes?.status === 'paid') {
                clearInterval(pollInterval);
                const w = await api.get<any>('/wallet');
                if (w?.wallet?.balance != null) balance.value = w.wallet.balance;
                setState('success');
                setTimeout(close, 3000);
              }
            } catch {}
          }, 5000);
          setTimeout(() => clearInterval(pollInterval), 1800000);
        }
        return;
      }
      if (res?.status === 'paid') {
        const w = await api.get<any>('/wallet');
        if (w?.wallet?.balance != null) balance.value = w.wallet.balance;
        setState('success');
        setTimeout(close, 3000);
      } else {
        setErrorMsg('Pagamento não confirmado. Tente novamente.');
        setState('error');
      }
    } catch (e: any) {
      setErrorMsg(e?.message || 'Erro ao processar pagamento. Tente novamente.');
      setState('error');
    }
  };

  // Prevenir scroll do body quando modal aberto
  useEffect(() => {
    if (isOpen) {
      document.body.classList.add('modal-open');
      return () => document.body.classList.remove('modal-open');
    }
  }, [isOpen]);

  if (!isOpen) return null;
  if (!ready && state === 'idle') return (
    <div style={S.overlay}><Spinner size="md" inline /></div>
  );

  const amounts = [5, 10, 25, 50];
  const fmtMin = (s: number) => `${Math.floor(s / 60).toString().padStart(2, '0')}:${(s % 60).toString().padStart(2, '0')}`;

  return (
    <div className="recharge-overlay" style={S.overlay} onClick={(e: any) => { if (e.target === e.currentTarget) close(); }}>
      <div className="recharge-modal" style={S.modal} onClick={(e: any) => e.stopPropagation()}>

        {/* Header */}
        <div style={S.header}>
          <div>
            <h2 style={S.title}>Adicionar créditos</h2>
            <p style={S.subtitle}>Saldo atual: <span style={{ color: T.success, fontWeight: 600 }}>${balance.value?.toFixed(2) ?? '0.00'}</span></p>
          </div>
          <button onClick={close} style={S.closeBtn} aria-label="Fechar">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>

        {/* Success */}
        {state === 'success' ? (
          <div style={{ textAlign: 'center', padding: '48px 0 32px' }}>
            <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'rgba(16,185,129,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke={T.success} strokeWidth="2.5" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>
            </div>
            <div style={{ fontSize: 20, fontWeight: 700, color: T.text, marginBottom: 8 }}>Pagamento confirmado!</div>
            <div style={{ fontSize: 14, color: T.textSecondary }}>Novo saldo: <span style={{ color: T.success, fontWeight: 700 }}>${balance.value?.toFixed(2)}</span></div>
            {isFirstDeposit && <div style={{ fontSize: 13, color: T.warning, fontWeight: 600, marginTop: 12, padding: '8px 16px', background: 'rgba(245,158,11,0.08)', borderRadius: 8, display: 'inline-block' }}>🎉 Bônus 2x aplicado no primeiro depósito!</div>}
          </div>

        ) : state === 'processing' ? (
          <div style={{ textAlign: 'center', padding: '56px 0', display: 'flex', flexDirection: 'column' as any, alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ marginBottom: 20, display: 'flex', justifyContent: 'center' }}><Spinner size="md" inline /></div>
            <div style={{ fontSize: 16, fontWeight: 600, color: T.text }}>Processando pagamento...</div>
            <div style={{ fontSize: 13, color: T.textSecondary, marginTop: 8 }}>Não feche esta janela</div>
          </div>

        ) : state === 'error' ? (
          <div style={{ textAlign: 'center', padding: '48px 0 32px' }}>
            <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'rgba(239,68,68,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke={T.danger} strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </div>
            <div style={{ fontSize: 18, fontWeight: 700, color: T.text, marginBottom: 8 }}>Pagamento não aprovado</div>
            <div style={{ fontSize: 13, color: T.textSecondary, marginBottom: 24, maxWidth: 280, margin: '0 auto 24px', lineHeight: 1.5 }}>{errorMsg}</div>
            <button onClick={() => setState('idle')} style={S.primaryBtn}>Tentar novamente</button>
          </div>

        ) : state === 'pix_waiting' ? (
          <div style={{ textAlign: 'center', padding: '16px 0 8px' }}>
            <div style={{ fontSize: 13, color: T.textSecondary, marginBottom: 20 }}>Escaneie o QR Code ou copie o código abaixo</div>

            {pixQrUrl && (
              <div style={{ background: '#fff', borderRadius: 16, padding: 16, display: 'inline-block', marginBottom: 20, boxShadow: '0 2px 12px rgba(0,0,0,0.1)' }}>
                <img src={pixQrUrl} alt="QR Code PIX" style={{ width: 180, height: 180, display: 'block' }} />
              </div>
            )}

            <div style={{ fontSize: 28, fontWeight: 800, color: T.text, marginBottom: 4 }}>
              ${amount}
            </div>
            <div style={{ fontSize: 13, color: T.textSecondary, marginBottom: 20 }}>
              ≈ R$ {(amount * brlRate).toFixed(2)}
            </div>

            {pixCode && (
              <>
                <div style={{ background: T.bgCard, padding: '12px 14px', borderRadius: 10, fontSize: 10, wordBreak: 'break-all' as any, marginBottom: 12, border: `1px solid ${T.border}`, fontFamily: 'monospace', lineHeight: 1.7, textAlign: 'left' as any, maxHeight: 72, overflowY: 'auto' as any }}>{pixCode}</div>
                <button onClick={() => { navigator.clipboard.writeText(pixCode); setCopied(true); setTimeout(() => setCopied(false), 2000); }} style={{ ...S.primaryBtn, background: copied ? T.success : S.primaryBtn.background }}>
                  {copied ? '✓ Código copiado!' : 'Copiar código PIX'}
                </button>
              </>
            )}

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginTop: 20 }}>
              <div style={{ width: 8, height: 8, borderRadius: '50%', background: T.warning, animation: 'pulse 2s ease-in-out infinite' }} />
              <span style={{ fontSize: 12, color: T.textSecondary }}>Aguardando pagamento · {fmtMin(pixTimer)}</span>
            </div>
          </div>

        ) : (
          <>
            {/* Seletor de valor — grid limpo estilo ChatGPT */}
            <div style={{ marginBottom: 24 }}>
              <label style={S.sectionLabel}>Valor</label>
              <div className="recharge-amounts-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                {amounts.map(a => {
                  const sel = amount === a;
                  return (
                    <button key={a} onClick={() => setAmount(a)} style={{
                      padding: '18px 12px', borderRadius: 12, cursor: 'pointer', textAlign: 'center' as any,
                      transition: 'all 0.15s ease',
                      background: sel ? 'rgba(255,255,255,0.08)' : 'transparent',
                      border: sel ? '1.5px solid rgba(255,255,255,0.25)' : '1px solid rgba(255,255,255,0.08)',
                      outline: 'none', width: '100%', minWidth: 0, boxSizing: 'border-box' as any,
                    }}>
                      <div style={{ fontSize: 22, fontWeight: 700, color: T.text, lineHeight: 1.2 }}>${a}</div>
                      <div style={{ fontSize: 12, color: T.textSecondary, marginTop: 4 }}>R$ {(a * brlRate).toFixed(0)}</div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Método de pagamento — segmented control estilo Apple */}
            <div style={{ marginBottom: 20 }}>
              <label style={S.sectionLabel}>Método de pagamento</label>
              <div style={{ display: 'flex', padding: 3, borderRadius: 10, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)' }}>
                {(['pix', 'card'] as const).map(t => (
                  <button key={t} onClick={() => setTab(t)} style={{
                    flex: 1, padding: '10px 0', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600,
                    cursor: 'pointer', transition: 'all 0.15s ease', outline: 'none',
                    background: tab === t ? 'rgba(255,255,255,0.1)' : 'transparent',
                    color: tab === t ? T.text : T.textSecondary,
                    boxShadow: tab === t ? '0 1px 3px rgba(0,0,0,0.2)' : 'none',
                  }}>
                    {t === 'pix' ? 'PIX' : 'Cartão'}
                  </button>
                ))}
              </div>
            </div>

            {/* Resumo */}
            <div style={{ textAlign: 'center' as any, marginBottom: 16, padding: '12px 0', borderTop: '1px solid rgba(255,255,255,0.06)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
              <span style={{ fontSize: 24, fontWeight: 800, color: T.text }}>${amount}</span>
              <span style={{ fontSize: 13, color: T.textSecondary, marginLeft: 8 }}>≈ R$ {(amount * brlRate).toFixed(2)}</span>
            </div>

            {tab === 'card' ? (
              <div>
                {cards.length > 0 && (
                  <div style={{ marginBottom: 12 }}>
                    {cards.map(c => {
                      const sel = !useNewCard && selectedCard === c.id;
                      return (
                        <div key={c.id} onClick={() => { setSelectedCard(c.id); setUseNewCard(false); }} style={{
                          display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', borderRadius: 10,
                          background: sel ? 'rgba(255,255,255,0.06)' : 'transparent',
                          border: sel ? '1.5px solid rgba(255,255,255,0.2)' : '1px solid rgba(255,255,255,0.06)',
                          marginBottom: 6, cursor: 'pointer', transition: 'all 0.12s',
                        }}>
                          <div style={{ width: 18, height: 18, borderRadius: '50%', border: sel ? `5px solid ${T.primary}` : '2px solid rgba(255,255,255,0.2)', background: sel ? T.primary : 'transparent', transition: 'all 0.12s', flexShrink: 0 }} />
                          <div>
                            <div style={{ fontSize: 14, fontWeight: 500, color: T.text }}>•••• {c.last4}</div>
                            <div style={{ fontSize: 11, color: T.textSecondary }}>{c.brand}</div>
                          </div>
                        </div>
                      );
                    })}
                    <div onClick={() => setUseNewCard(true)} style={{
                      display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', borderRadius: 10,
                      background: useNewCard ? 'rgba(255,255,255,0.06)' : 'transparent',
                      border: useNewCard ? '1.5px solid rgba(255,255,255,0.2)' : '1px solid rgba(255,255,255,0.06)',
                      marginBottom: 6, cursor: 'pointer', transition: 'all 0.12s',
                    }}>
                      <div style={{ width: 18, height: 18, borderRadius: '50%', border: useNewCard ? `5px solid ${T.primary}` : '2px solid rgba(255,255,255,0.2)', background: useNewCard ? T.primary : 'transparent', transition: 'all 0.12s', flexShrink: 0 }} />
                      <div style={{ fontSize: 14, fontWeight: 500, color: T.text }}>Usar outro cartão</div>
                    </div>
                  </div>
                )}
                {(useNewCard || cards.length === 0) && (
                  <div style={{ display: 'flex', flexDirection: 'column' as any, gap: 8, marginBottom: 12 }}>
                    <input placeholder="Número do cartão" value={cardForm.number} onInput={(e: any) => setCardForm({ ...cardForm, number: e.target.value })} style={S.input} />
                    <div style={{ display: 'flex', gap: 8 }}>
                      <input placeholder="MM" value={cardForm.exp_month} onInput={(e: any) => setCardForm({ ...cardForm, exp_month: e.target.value })} style={{ ...S.input, flex: 1 }} maxLength={2} />
                      <input placeholder="AA" value={cardForm.exp_year} onInput={(e: any) => setCardForm({ ...cardForm, exp_year: e.target.value })} style={{ ...S.input, flex: 1 }} maxLength={2} />
                      <input placeholder="CVV" value={cardForm.cvv} onInput={(e: any) => setCardForm({ ...cardForm, cvv: e.target.value })} style={{ ...S.input, flex: 1 }} maxLength={4} />
                    </div>
                    <input placeholder="Nome impresso no cartão" value={cardForm.holder_name} onInput={(e: any) => setCardForm({ ...cardForm, holder_name: e.target.value })} style={S.input} />
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: T.textSecondary, cursor: 'pointer' }}>
                      <input type="checkbox" checked={saveCard} onChange={() => setSaveCard(!saveCard)} style={{ accentColor: T.primary }} />
                      Salvar para próximas compras
                    </label>
                  </div>
                )}
                <button onClick={handlePay} style={S.primaryBtn}>Pagar ${amount}</button>
              </div>
            ) : (
              <button onClick={handlePay} style={S.primaryBtn}>Gerar PIX</button>
            )}

            {/* Trust */}
            <div style={{ display: 'flex', justifyContent: 'center', gap: 20, marginTop: 20, paddingTop: 16 }}>
              <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.25)', display: 'flex', alignItems: 'center', gap: 5 }}>
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                Pagamento seguro
              </span>
              <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.25)', display: 'flex', alignItems: 'center', gap: 5 }}>
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>
                Créditos instantâneos
              </span>
            </div>
          </>
        )}
      </div>

      <style>{`
        @keyframes rechargeOverlayIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes rechargeModalIn {
          from { opacity: 0; transform: scale(0.95) translateY(10px); }
          to { opacity: 1; transform: scale(1) translateY(0); }
        }
        .recharge-amounts-grid {
          grid-template-columns: 1fr 1fr !important;
        }
        @media (max-width: 640px) {
          .recharge-overlay {
            padding: 0 !important;
            align-items: flex-end !important;
          }
          .recharge-modal {
            max-width: 100% !important;
            width: 100% !important;
            max-height: 100vh !important;
            height: 100vh !important;
            border-radius: 0 !important;
            border: none !important;
            padding: 24px 20px !important;
            animation: rechargeSlideUp 0.25s ease !important;
          }
          .recharge-amounts-grid {
            grid-template-columns: 1fr 1fr !important;
          }
        }
        @keyframes rechargeSlideUp {
          from { opacity: 0; transform: translateY(100%); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}

/* ── Estilos centralizados ── */
const S = {
  overlay: {
    position: 'fixed' as any, inset: 0, zIndex: 9999999,
    background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(8px)',
    WebkitBackdropFilter: 'blur(8px)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    animation: 'rechargeOverlayIn 0.2s ease', padding: 16,
  },
  modal: {
    background: '#1e1e1e', borderRadius: 16, maxWidth: 420, width: '100%',
    padding: '28px 24px', position: 'relative' as any,
    animation: 'rechargeModalIn 0.2s ease',
    maxHeight: '90vh', overflowY: 'auto' as any,
    color: T.text, border: '1px solid rgba(255,255,255,0.08)',
    boxShadow: '0 24px 80px rgba(0,0,0,0.5)',
  },
  header: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
    marginBottom: 24,
  },
  title: {
    fontSize: 20, fontWeight: 700, color: T.text, margin: 0, lineHeight: 1.3,
  } as any,
  subtitle: {
    fontSize: 13, color: T.textSecondary, margin: '4px 0 0', lineHeight: 1.4,
  } as any,
  closeBtn: {
    background: 'none', border: 'none', color: T.textSecondary, cursor: 'pointer',
    padding: 4, borderRadius: 8, transition: 'color 0.12s',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    flexShrink: 0,
  },
  sectionLabel: {
    fontSize: 12, fontWeight: 600, color: T.textSecondary,
    textTransform: 'uppercase' as any, letterSpacing: '0.5px',
    display: 'block', marginBottom: 10,
  },
  primaryBtn: {
    padding: '14px', borderRadius: 10,
    background: 'linear-gradient(135deg, #2563eb, #7c3aed)',
    color: '#fff', border: 'none', fontSize: 15, fontWeight: 700,
    cursor: 'pointer', width: '100%', transition: 'opacity 0.15s',
    boxShadow: '0 2px 12px rgba(37,99,235,0.3)',
  },
  input: {
    width: '100%', padding: '12px 14px', borderRadius: 10,
    border: '1px solid rgba(255,255,255,0.1)',
    background: 'rgba(255,255,255,0.04)', color: T.text,
    fontSize: 14, outline: 'none', boxSizing: 'border-box' as any,
    transition: 'border-color 0.15s',
  },
};
